import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCalendarContext } from '../contexts/CalendarContext';
import api from '../services/api';
import './CalendarPanel.css';

// Makes a centered overlay window draggable by a handle (e.g. its header).
// Returns { pos, style, onHandleMouseDown, reset }. `pos` is a translate offset
// applied on top of the flex-centered position.
const useDraggable = () => {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null); // { startX, startY, baseX, baseY }

  const onHandleMouseDown = useCallback((e) => {
    // Ignore clicks on buttons/inputs inside the handle (e.g. the × close).
    if (e.button !== 0 || e.target.closest('button, input, textarea, select, a')) return;
    e.preventDefault();
    drag.current = { startX: e.clientX, startY: e.clientY, baseX: pos.x, baseY: pos.y };

    const onMove = (ev) => {
      if (!drag.current) return;
      setPos({
        x: drag.current.baseX + (ev.clientX - drag.current.startX),
        y: drag.current.baseY + (ev.clientY - drag.current.startY),
      });
    };
    const onUp = () => {
      drag.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pos]);

  const reset = useCallback(() => setPos({ x: 0, y: 0 }), []);
  const style = { transform: `translate(${pos.x}px, ${pos.y}px)` };
  return { pos, style, onHandleMouseDown, reset };
};

// Emoji icon for an attachment based on its MIME type.
const attIcon = (mime) => {
  if (!mime) return '📎';
  if (mime.startsWith('image/')) return '🖼️';
  if (mime === 'application/pdf') return '📄';
  if (mime.includes('word')) return '📝';
  if (mime.includes('sheet') || mime.includes('excel')) return '📊';
  return '📎';
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

// Event Modal Component
const EventModal = () => {
  const {
    isModalOpen,
    selectedEvent,
    closeModal,
    createEvent,
    updateEvent,
    deleteEvent,
  } = useCalendarContext();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'meeting',
    start: '',
    end: '',
    location: '',
    status: 'Planned'
  });
  const [saving, setSaving] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const modalDrag = useDraggable();
  const descDrag = useDraggable();

  // Reset the modal's dragged position each time it opens.
  useEffect(() => { if (isModalOpen) modalDrag.reset(); }, [isModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  // Reset the description window's position each time it opens.
  useEffect(() => { if (descExpanded) descDrag.reset(); }, [descExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show the parent record's CURRENT attachments live (e.g. the Lead this
  // meeting was scheduled from), following the meeting's parent link.
  useEffect(() => {
    const parentType = selectedEvent && selectedEvent.parent_type;
    const parentId = selectedEvent && selectedEvent.parent_id;
    if (!isModalOpen || !parentType || !parentId) { setAttachments([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = await api.ensureAuthenticated();
        const res = await fetch(
          `/api/chat/ledger-attachments.php?parent_type=${encodeURIComponent(parentType)}&parent_id=${encodeURIComponent(parentId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (!cancelled && data.success) setAttachments(data.data || []);
      } catch (e) { if (!cancelled) setAttachments([]); }
    })();
    return () => { cancelled = true; };
  }, [isModalOpen, selectedEvent]);

  useEffect(() => {
    if (selectedEvent) {
      setFormData({
        id: selectedEvent.id || '',
        title: selectedEvent.title || '',
        description: selectedEvent.description || '',
        type: selectedEvent.type || 'meeting',
        start: selectedEvent.start ? formatDateTimeLocal(selectedEvent.start) : '',
        end: selectedEvent.end ? formatDateTimeLocal(selectedEvent.end) : '',
        location: selectedEvent.location || '',
        status: selectedEvent.status || 'Planned'
      });
    }
  }, [selectedEvent]);

  const formatDateTimeLocal = (dateStr) => {
    if (!dateStr) return '';
    // Meeting times are stored as naive local wall-clock, so reformat to
    // datetime-local WITHOUT any timezone conversion (toISOString would shift
    // the value to UTC and show the wrong hour).
    const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (formData.id) {
        await updateEvent(formData);
      } else {
        await createEvent(formData);
      }
      closeModal();
    } catch (err) {
      alert('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!formData.id) return;
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    setSaving(true);
    try {
      await deleteEvent(formData.id, formData.type);
      closeModal();
    } catch (err) {
      alert('Failed to delete event');
    } finally {
      setSaving(false);
    }
  };

  if (!isModalOpen) return null;

  return (
    <div className="calendar-modal-overlay" onClick={closeModal}>
      <div className="calendar-modal" style={modalDrag.style} onClick={e => e.stopPropagation()}>
        <div
          className="calendar-modal-header calendar-drag-handle"
          onMouseDown={modalDrag.onHandleMouseDown}
        >
          <h3 className="calendar-modal-title">
            {formData.id ? 'Edit Event' : 'New Event'}
          </h3>
          <button className="calendar-modal-close" onClick={closeModal}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="calendar-modal-body">
            <div className="calendar-type-selector">
              <button
                type="button"
                className={`calendar-type-btn meeting ${formData.type === 'meeting' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, type: 'meeting' }))}
              >
                📅 Meeting
              </button>
              <button
                type="button"
                className={`calendar-type-btn call ${formData.type === 'call' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, type: 'call' }))}
              >
                📞 Call
              </button>
              <button
                type="button"
                className={`calendar-type-btn task ${formData.type === 'task' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, type: 'task' }))}
              >
                ✓ Task
              </button>
            </div>

            <div className="calendar-form-group">
              <label className="calendar-form-label">Title</label>
              <input
                type="text"
                name="title"
                className="calendar-form-input"
                value={formData.title}
                onChange={handleChange}
                placeholder="Add title"
                required
              />
            </div>

            <div className="calendar-form-row">
              <div className="calendar-form-group">
                <label className="calendar-form-label">Start</label>
                <input
                  type="datetime-local"
                  name="start"
                  className="calendar-form-input"
                  value={formData.start}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="calendar-form-group">
                <label className="calendar-form-label">End</label>
                <input
                  type="datetime-local"
                  name="end"
                  className="calendar-form-input"
                  value={formData.end}
                  onChange={handleChange}
                />
              </div>
            </div>

            {formData.type === 'meeting' && (
              <div className="calendar-form-group">
                <label className="calendar-form-label">Location</label>
                <input
                  type="text"
                  name="location"
                  className="calendar-form-input"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Add location"
                />
              </div>
            )}

            <div className="calendar-form-group">
              <label className="calendar-form-label">Status</label>
              <select
                name="status"
                className="calendar-form-select"
                value={formData.status}
                onChange={handleChange}
              >
                {formData.type === 'task' ? (
                  <>
                    <option value="Not Started">Not Started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Pending Input">Pending Input</option>
                    <option value="Deferred">Deferred</option>
                  </>
                ) : (
                  <>
                    <option value="Planned">Planned</option>
                    <option value="Held">Held</option>
                    <option value="Not Held">Not Held</option>
                  </>
                )}
              </select>
            </div>

            <div className="calendar-form-group">
              <div className="calendar-label-row">
                <label className="calendar-form-label">Description</label>
                <button
                  type="button"
                  className="calendar-expand-btn"
                  onClick={() => setDescExpanded(true)}
                  title="Expand description"
                  aria-label="Expand description"
                >
                  ⤢ Expand
                </button>
              </div>
              <textarea
                name="description"
                className="calendar-form-textarea"
                value={formData.description}
                onChange={handleChange}
                placeholder="Add description"
              />
            </div>

            {formData.type === 'meeting' && attachments.length > 0 && (
              <div className="calendar-form-group">
                <label className="calendar-form-label">Attachments</label>
                <ul className="calendar-attachments-list">
                  {attachments.map(att => (
                    <li key={att.id} className="calendar-attachment">
                      <a href={att.file_url} target="_blank" rel="noopener noreferrer" title={att.name}>
                        <span className="att-icon">{attIcon(att.mime)}</span>
                        <span className="att-name">{att.name}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="calendar-modal-footer">
            {formData.id && (
              <button
                type="button"
                className="calendar-btn calendar-btn-danger"
                onClick={handleDelete}
                disabled={saving}
              >
                Delete
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button
              type="button"
              className="calendar-btn calendar-btn-secondary"
              onClick={closeModal}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="calendar-btn calendar-btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : (formData.id ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>

      {descExpanded && (
        <div
          className="calendar-desc-overlay"
          onClick={(e) => { e.stopPropagation(); setDescExpanded(false); }}
        >
          <div className="calendar-desc-window" style={descDrag.style} onClick={(e) => e.stopPropagation()}>
            <div
              className="calendar-desc-header calendar-drag-handle"
              onMouseDown={descDrag.onHandleMouseDown}
            >
              <h3 className="calendar-desc-title">Description</h3>
              <button
                type="button"
                className="calendar-modal-close"
                onClick={() => setDescExpanded(false)}
                aria-label="Close expanded description"
              >
                ×
              </button>
            </div>
            <textarea
              name="description"
              className="calendar-desc-textarea"
              value={formData.description}
              onChange={handleChange}
              placeholder="Add description"
              autoFocus
            />
            <div className="calendar-desc-footer">
              <button
                type="button"
                className="calendar-btn calendar-btn-primary"
                onClick={() => setDescExpanded(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Notification Component
const CalendarNotifications = () => {
  const { notifications, removeNotification } = useCalendarContext();

  return (
    <>
      {notifications.map(notification => (
        <div key={notification.id} className="calendar-notification">
          <span className="calendar-notification-icon">
            {notification.type === 'meeting' ? '📅' :
             notification.type === 'call' ? '📞' : '✓'}
          </span>
          <div className="calendar-notification-content">
            <div className="calendar-notification-title">{notification.title}</div>
            <div className="calendar-notification-message">{notification.message}</div>
          </div>
          <button
            className="calendar-notification-close"
            onClick={() => removeNotification(notification.id)}
          >
            ×
          </button>
        </div>
      ))}
    </>
  );
};

// Main Calendar Panel Component
const CalendarPanel = () => {
  const {
    isPanelOpen,
    currentDate,
    events,
    loading,
    view,
    closePanel,
    navigateMonth,
    goToToday,
    setView,
    openModal,
  } = useCalendarContext();

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const getWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getHours = () => {
    const hours = [];
    for (let i = 0; i < 24; i++) {
      hours.push(i);
    }
    return hours;
  };

  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return '12 PM';
    return `${hour - 12} PM`;
  };

  const getEventHour = (event) => {
    if (!event.start) return 0;
    const timePart = event.start.includes('T')
      ? event.start.split('T')[1]
      : event.start.split(' ')[1];
    return parseInt(timePart?.split(':')[0] || '0', 10);
  };

  const getEventsForDate = (date) => {
    // Compare on the LOCAL calendar date (toISOString would use UTC and can
    // land events on the wrong day near midnight).
    const p = (n) => String(n).padStart(2, '0');
    const dateStr = `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
    return events.filter(event => {
      if (!event.start) return false;
      // Handle both "2026-01-16T08:55:00" and "2026-01-16 08:55:00" formats
      const eventDate = event.start.includes('T')
        ? event.start.split('T')[0]
        : event.start.split(' ')[0];
      return eventDate === dateStr;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const handleDayClick = (date) => {
    openModal(null, date);
  };

  const handleEventClick = (e, event) => {
    e.stopPropagation();
    openModal(event);
  };

  if (!isPanelOpen) return null;

  const days = getCalendarDays();

  return (
    <div className="calendar-panel">
      <div className="calendar-header">
        <div className="calendar-header-left">
          <h2 className="calendar-title">Calendar</h2>
          <button className="calendar-add-btn" onClick={() => openModal()}>
            <span className="calendar-add-btn-icon">+</span>
            Create
          </button>
        </div>
        <button className="calendar-close-btn" onClick={closePanel}>×</button>
      </div>

      <div className="calendar-nav">
        <button className="calendar-nav-btn" onClick={goToToday}>Today</button>
        <div className="calendar-nav-arrows">
          <button className="calendar-arrow-btn" onClick={() => navigateMonth(-1)}>‹</button>
          <button className="calendar-arrow-btn" onClick={() => navigateMonth(1)}>›</button>
        </div>
        <span className="calendar-current-month">
          {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
        </span>
        <div className="calendar-view-toggle">
          <button
            className={`calendar-view-btn ${view === 'month' ? 'active' : ''}`}
            onClick={() => setView('month')}
          >
            Month
          </button>
          <button
            className={`calendar-view-btn ${view === 'week' ? 'active' : ''}`}
            onClick={() => setView('week')}
          >
            Week
          </button>
          <button
            className={`calendar-view-btn ${view === 'day' ? 'active' : ''}`}
            onClick={() => setView('day')}
          >
            Day
          </button>
        </div>
      </div>

      <div className="calendar-body">
        {loading ? (
          <div className="calendar-loading">Loading calendar...</div>
        ) : view === 'month' ? (
          <div className="calendar-grid">
            {WEEKDAYS.map(day => (
              <div key={day} className="calendar-weekday-header">{day}</div>
            ))}

            {days.map((date, index) => {
              const dayEvents = getEventsForDate(date);
              const maxVisible = 3;

              return (
                <div
                  key={index}
                  className={`calendar-day ${!isCurrentMonth(date) ? 'other-month' : ''} ${isToday(date) ? 'today' : ''}`}
                  onClick={() => handleDayClick(date)}
                >
                  <span className="calendar-day-number">{date.getDate()}</span>
                  <div className="calendar-day-events">
                    {dayEvents.slice(0, maxVisible).map(event => (
                      <div
                        key={event.id}
                        className={`calendar-event ${event.type}`}
                        style={{ backgroundColor: event.color }}
                        onClick={(e) => handleEventClick(e, event)}
                        title={event.title}
                      >
                        {event.title}
                      </div>
                    ))}
                    {dayEvents.length > maxVisible && (
                      <div className="calendar-event-more">
                        +{dayEvents.length - maxVisible} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : view === 'week' ? (
          <div className="calendar-week-view">
            <div className="calendar-week-header">
              <div className="calendar-time-gutter"></div>
              {getWeekDays().map((date, index) => (
                <div key={index} className={`calendar-week-day-header ${isToday(date) ? 'today' : ''}`}>
                  <span className="calendar-week-day-name">{WEEKDAYS[date.getDay()]}</span>
                  <span className="calendar-week-day-number">{date.getDate()}</span>
                </div>
              ))}
            </div>
            <div className="calendar-week-body">
              {getHours().map(hour => (
                <div key={hour} className="calendar-hour-row">
                  <div className="calendar-time-label">{formatHour(hour)}</div>
                  {getWeekDays().map((date, dayIndex) => {
                    const dayEvents = getEventsForDate(date).filter(e => getEventHour(e) === hour);
                    return (
                      <div
                        key={dayIndex}
                        className={`calendar-hour-cell ${isToday(date) ? 'today' : ''}`}
                        onClick={() => {
                          const clickDate = new Date(date);
                          clickDate.setHours(hour);
                          openModal(null, clickDate);
                        }}
                      >
                        {dayEvents.map(event => (
                          <div
                            key={event.id}
                            className={`calendar-week-event ${event.type}`}
                            style={{ backgroundColor: event.color }}
                            onClick={(e) => handleEventClick(e, event)}
                            title={event.title}
                          >
                            {event.title}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="calendar-day-view">
            <div className="calendar-day-view-header">
              <span className="calendar-day-view-date">
                {WEEKDAYS[currentDate.getDay()]}, {MONTHS[currentDate.getMonth()]} {currentDate.getDate()}
              </span>
            </div>
            <div className="calendar-day-view-body">
              {getHours().map(hour => {
                const hourEvents = getEventsForDate(currentDate).filter(e => getEventHour(e) === hour);
                return (
                  <div key={hour} className="calendar-day-hour-row">
                    <div className="calendar-time-label">{formatHour(hour)}</div>
                    <div
                      className="calendar-day-hour-cell"
                      onClick={() => {
                        const clickDate = new Date(currentDate);
                        clickDate.setHours(hour);
                        openModal(null, clickDate);
                      }}
                    >
                      {hourEvents.map(event => (
                        <div
                          key={event.id}
                          className={`calendar-day-event ${event.type}`}
                          style={{ backgroundColor: event.color }}
                          onClick={(e) => handleEventClick(e, event)}
                        >
                          <span className="calendar-day-event-time">
                            {event.start?.split(' ')[1]?.slice(0, 5) || event.start?.split('T')[1]?.slice(0, 5)}
                          </span>
                          <span className="calendar-day-event-title">{event.title}</span>
                          {event.location && <span className="calendar-day-event-location">{event.location}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <EventModal />
      <CalendarNotifications />
    </div>
  );
};

export default CalendarPanel;
